import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './CheckInRowPage.css';
import { flightApi } from '../utils/axiosConfig';
import { useParams } from 'react-router-dom';
import { CheckInDesks, FlightDetails } from '../interface/flight.details';
import { Socket } from 'socket.io-client'
import AnimatedCodeshare from '../components/AnimatedCodeshare';
import { DeviceDetails } from '../interface/device.details';
import { useSocket } from '../context/SocketContext';
import { useAdSequence } from '../hooks/useAdSequence';
import { AdvertisementType } from '@industronics/fids-utils';
import AdvertisementDisplay from '../components/AdvertisementDisplay';
import { apiRoutes } from '../utils/apiRoutes';

interface CheckInRowPageProp {
  device: DeviceDetails
}

const CheckInRowPage: React.FC<CheckInRowPageProp> = ({ device }) => {
  const socketRef = useRef<Socket | null>(null)
  const [flights, setFlights] = useState<FlightDetails[]>([]);
  const { socket } = useSocket();

  const { currentAd } = useAdSequence(device.advertisement);

  const isFullScreenAd = currentAd &&
    !currentAd.ratio &&
    currentAd.advertisementType === AdvertisementType.FullScreen &&
    currentAd.type === 'ad';

  const getCheckinStatus = (openingTime: Date, closingTime: Date) => {
    const now = new Date().getTime();
    const open = new Date(openingTime).getTime();
    const close = new Date(closingTime).getTime();

    if (now < open) {
      const openTime = new Date(openingTime);
      const hours = openTime.getHours().toString().padStart(2, '0');
      const minutes = openTime.getMinutes().toString().padStart(2, '0');
      return `Opens at ${hours}:${minutes}`;
    }

    if (now >= open && now <= close) {
      return 'OPEN';
    }

    return 'CLOSED';
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = Date.now();

      setFlights((prevFlights) => {
        let updatedFlights: FlightDetails[] = [];
        updatedFlights = [...prevFlights];
        updatedFlights = updatedFlights
          .filter(item => {
            return !item.isDeleted && item.isDisplay &&
              (new Date(item.effectiveTime).getTime() > now);
          })
        return updatedFlights;
      });

    }, 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await flightApi.get(apiRoutes.flightTrips.getFlightTrips);
        const data = response.data?.data;

        if (Array.isArray(data)) {
          setFlights(data);
        } else {
          console.warn('Invalid API response format:', response.data);
          setFlights([]);
        }
      } catch (error) {
        console.error('Error fetching flights:', error);
        setFlights([]);
      }
    };

    fetchData();
  }, []);

  const getCheckinRange = (checkInDesks: CheckInDesks[]) => {

    if (!checkInDesks || !device.checkInRow) return;

    const filtered = checkInDesks.filter(entry => entry.counter.startsWith(device.checkInRow));

    if (filtered.length === 0) return null;

    const numbers = filtered.map(item => parseInt(item.counter.slice(1))).sort((a, b) => a - b);

    const start = `${device.checkInRow}${numbers[0]}`;
    const end = `${device.checkInRow}${numbers[numbers.length - 1]}`;

    return `${start} - ${end}`;
  };

  useEffect(() => {
    if (!socket) return;

    const handleFlightUpdate =
      (flightData: FlightDetails) => {
        if (device.checkInRow && flightData.checkInRow?.includes(device.checkInRow)) {
          setFlights((prev) => {
            const currentTime = Date.now()

            const index = prev.findIndex((x) => x._id === flightData._id)
            let updatedFlights = [...prev]

            if (index === -1) {
              updatedFlights.push(flightData)
            } else {
              updatedFlights[index] = flightData
            }

            return updatedFlights.filter(
              (item) =>
                !item.isDeleted &&
                item.isDisplay &&
                (new Date(item.effectiveTime).getTime() >
                  currentTime)
            )
          })
        } else {
          setFlights((prev) => prev.filter((x) => x._id !== flightData._id))
        }
      };

    const tripListener = () => {
      socket.off("flight.update", handleFlightUpdate);
      socket.on("flight.update", handleFlightUpdate);
    };

    const handleConnect = () => {
      tripListener();
    };

    socket.on("connect", handleConnect);

    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("flight.update", handleFlightUpdate);
    };
  }, [socket]);

  return (
    <div className="rowg-background text-white d-flex flex-column align-items-center py-3">
      {isFullScreenAd ? (
        <>
          <h1 className="row-title">ROW {device.checkInRow}</h1><div className="container">
            {flights.map((flight) => {
              const status = getCheckinStatus(
                flight.checkInRowOpeningTime,
                flight.checkInRowClosingTime
              );
              const checkinRange = getCheckinRange(flight.checkInDesks!);

              return (
                <div key={flight.flightNumber} className="d-flex flex-row border-top border-light">
                  {/* Airline logo */}
                  <div className="me-4">
                    <AnimatedCodeshare labelClassName='fs-md' codeShareFlights={[flight.flightNumber, ...flight.codeShareFlight]} />
                  </div>

                  {/* Flight details */}
                  <div className="d-flex flex-column">
                    <div className={`fs-1 fw-bold ${status === 'OPEN' ? 'text-info' :
                      status.startsWith('Opens at') ? 'text-warning' : 'text-danger'}`}>
                      {status}
                    </div>

                    <div className="fs-2 fw-semibold">{checkinRange}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>) : (
        <AdvertisementDisplay advertisement={currentAd} isFullScreen={true} />
      )}
    </div>
  );
};

export default CheckInRowPage;
